import type { ReactNode } from 'react';

import type { Placement } from '@floating-ui/react';
import type { ColorName } from 'styles/config/colors';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';
import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';

import HelpText from 'components/atoms/HelpText';
import InputError from 'components/atoms/InputError';
import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';

export type InputWrapperProps = {
  label?: ReactNode;
  labelAsDiv?: boolean;
  labelColor?: ColorName;
  rightLabel?: ReactNode;
  errors?: string[];
  showErrorAsTooltip?: boolean;
  errorIcon?: ReactNode;
  afterInput?: ReactNode;
  afterErrors?: ReactNode;
  help?: ReactNode;
  'data-test-id'?: string;
  'data-pendo-id'?: string;
  'data-test-id-tooltip'?: string;
  'data-dd-action'?: string;
  fullWidth?: boolean;
  portal?: boolean;
  inverse?: boolean;
  className?: string;
  helpPlacement?: Placement;
};

export const InputWrapper: ComponentWithChildren<InputWrapperProps> = ({
  label,
  rightLabel,
  labelColor,
  errors = [],
  showErrorAsTooltip,
  errorIcon,
  help,
  afterInput,
  afterErrors,
  labelAsDiv,
  children,
  'data-test-id': dataTestId = 'input-wrapper',
  'data-pendo-id': pendoId,
  'data-test-id-tooltip': tooltipTestId = 'tooltip-text',
  'data-dd-action': ddActionName,
  fullWidth,
  portal,
  inverse,
  className,
  helpPlacement = 'top-start',
}) => {
  const LabelTag = (labelAsDiv ? 'div' : 'label') as 'label';

  return (
    <ConditionalWrapper
      condition={!!label || !!rightLabel}
      wrapper={children => (
        <LabelTag
          className={cn(
            'relative flex flex-col',
            fullWidth && 'w-full',
            className,
          )}
          data-test-id={dataTestId}
          data-pendo-id={pendoId}
        >
          <div
            className={cn(
              'flex items-center justify-between text-body-small',
              errorIcon ? 'mb-0 [&_p]:mb-0' : 'mb-xs1',
              inverse ? 'text-primary-inverse' : 'text-secondary',
            )}
            data-test-id="label-container"
            style={
              !inverse && labelColor
                ? { color: colorCssVar(labelColor) }
                : undefined
            }
          >
            <HelpText
              className="help-text"
              data-test-id={tooltipTestId}
              help={help!}
              placement={helpPlacement}
              portal={portal}
            >
              {label}
            </HelpText>
            {rightLabel && <span>{rightLabel}</span>}
          </div>
          {children}
        </LabelTag>
      )}
    >
      <ConditionalWrapper
        condition={!!afterInput}
        wrapper={children => (
          <div
            className={cn('flex items-center', fullWidth && 'w-full')}
            data-dd-action={ddActionName}
            data-test-id="input-with-end-component"
          >
            {children}
            {afterInput}
          </div>
        )}
      >
        {children}
      </ConditionalWrapper>
      {!!errors?.length && (
        <div className="mt-xs2">
          <InputError
            errors={errors}
            icon={errorIcon}
            showErrorAsTooltip={showErrorAsTooltip}
          />
        </div>
      )}
      {afterErrors}
    </ConditionalWrapper>
  );
};

export default InputWrapper;
